import React, { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, GripVertical, Camera, ExternalLink, Plus, Minus, ChevronLeft, ChevronRight, Maximize, Minimize2, Download } from 'lucide-react'
import { Scene } from '../../types'
import { useProjectStore } from '../../stores/projectStore'
import CreateSceneDialog from './CreateSceneDialog'

interface ScenesOverviewDialogProps {
  open: boolean
  onClose: () => void
  scenes: Scene[]
  projectId: string
  projectAspectRatio: string
  onSceneSelect: (sceneId: string) => void
  onExport?: () => void
}

const ScenesOverviewDialog: React.FC<ScenesOverviewDialogProps> = ({
  open,
  onClose,
  scenes,
  projectId,
  projectAspectRatio,
  onSceneSelect,
  onExport
}) => {
  const [draggingSceneId, setDraggingSceneId] = useState<string | null>(null)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [fitToScreen, setFitToScreen] = useState(false)
  const [thumbnailScale, setThumbnailScale] = useState(1) // 0.5 to 2.0
  const [showCreateScene, setShowCreateScene] = useState(false)
  const { reorderScenes } = useProjectStore()

  // Get scenes with thumbnails for lightbox navigation
  const scenesWithThumbnails = scenes.filter(s => s.thumbnailUrl)
  const currentLightboxScene = lightboxIndex !== null ? scenesWithThumbnails[lightboxIndex] : null

  const handlePrevImage = useCallback(() => {
    if (lightboxIndex !== null && lightboxIndex > 0) {
      setLightboxIndex(lightboxIndex - 1)
    }
  }, [lightboxIndex])

  const handleNextImage = useCallback(() => {
    if (lightboxIndex !== null && lightboxIndex < scenesWithThumbnails.length - 1) {
      setLightboxIndex(lightboxIndex + 1)
    }
  }, [lightboxIndex, scenesWithThumbnails.length])

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (lightboxIndex !== null) {
          setLightboxIndex(null)
        } else {
          onClose()
        }
      } else if (lightboxIndex !== null) {
        if (e.key === 'ArrowLeft') {
          handlePrevImage()
        } else if (e.key === 'ArrowRight') {
          handleNextImage()
        }
      }
    }
    if (open) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, onClose, lightboxIndex, handlePrevImage, handleNextImage])

  const openLightbox = (scene: Scene) => {
    const index = scenesWithThumbnails.findIndex(s => s.id === scene.id)
    if (index !== -1) {
      setLightboxIndex(index)
    }
  }

  const handleDragStart = (e: React.DragEvent, sceneId: string) => {
    e.dataTransfer.setData('sceneId', sceneId)
    e.dataTransfer.effectAllowed = 'move'
    setDraggingSceneId(sceneId)
  }

  const handleDragEnd = () => {
    setDraggingSceneId(null)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDrop = async (e: React.DragEvent, targetSceneId: string) => {
    e.preventDefault()
    setDraggingSceneId(null)

    const draggedSceneId = e.dataTransfer.getData('sceneId')
    if (!draggedSceneId || draggedSceneId === targetSceneId) return

    const draggedIndex = scenes.findIndex(s => s.id === draggedSceneId)
    const targetIndex = scenes.findIndex(s => s.id === targetSceneId)

    if (draggedIndex === -1 || targetIndex === -1) return

    const newScenes = [...scenes]
    const [draggedScene] = newScenes.splice(draggedIndex, 1)
    newScenes.splice(targetIndex, 0, draggedScene)

    try {
      await reorderScenes(projectId, newScenes.map(s => s.id))
    } catch (error) {
      console.error('Failed to reorder scenes:', error)
    }
  }

  const getAspectRatio = () => {
    switch (projectAspectRatio) {
      case '16:9': return '16/9'
      case '4:3': return '4/3'
      case '1:1': return '1/1'
      case '9:16': return '9/16'
      default: return '16/9'
    }
  }

  // Calculate grid thumbnail size based on scale
  const baseSize = 200
  const scaledSize = Math.round(baseSize * thumbnailScale)

  if (!open) return null

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="glass-card w-full h-full max-w-[95vw] max-h-[95vh] m-4 overflow-hidden flex flex-col"
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with controls - same style as gallery */}
          <div className="flex items-center justify-between p-4 border-b border-glass-border">
            <div>
              <h2 className="text-xl font-headline text-white font-semibold">Scenes Overview</h2>
              <p className="text-white/50 text-sm">
                {scenes.length} {scenes.length === 1 ? 'scene' : 'scenes'} • Drag to reorder
              </p>
            </div>

            <div className="flex items-center space-x-4">
              {/* Thumbnail Size Slider - same as gallery */}
              <div className="glass-card p-1 flex items-center space-x-2 px-3 h-9" title="Thumbnail Size">
                <Minus className="w-3 h-3 text-white/40" />
                <input
                  type="range"
                  min="0.5"
                  max="2.0"
                  step="0.1"
                  value={thumbnailScale}
                  onChange={(e) => setThumbnailScale(parseFloat(e.target.value))}
                  className="w-32 accent-purple-500"
                />
                <Plus className="w-3 h-3 text-white/40" />
              </div>

              {/* Export Button */}
              {onExport && (
                <button
                  onClick={onExport}
                  className="glass-button bg-green-500/30 hover:bg-green-500/40 text-white px-4 py-2 flex items-center space-x-2"
                >
                  <Download className="w-4 h-4" />
                  <span>Export</span>
                </button>
              )}

              <button
                onClick={onClose}
                className="glass-button p-2 hover:bg-white/10"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Grid Content - same layout as ImageGallery */}
          <div className="flex-1 overflow-y-auto p-4">
            <div 
              className="grid gap-4"
              style={{
                gridTemplateColumns: `repeat(auto-fill, minmax(${scaledSize}px, 1fr))`
              }}
            >
              {/* Add Scene Card - First in grid */}
              <motion.div
                className="group relative glass-card overflow-hidden border-2 border-dashed border-white/20 hover:border-purple-500/50 transition-all cursor-pointer"
                onClick={() => setShowCreateScene(true)}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <div 
                  className="w-full flex items-center justify-center bg-purple-500/10 hover:bg-purple-500/20 transition-colors"
                  style={{ aspectRatio: getAspectRatio() }}
                >
                  <div className="text-center">
                    <Plus className="w-12 h-12 text-purple-400/60 mx-auto mb-2" />
                    <p className="text-white/70 font-medium text-sm">Add Scene</p>
                  </div>
                </div>
              </motion.div>

              {/* Scene Cards */}
              {scenes.map((scene, index) => (
                <div
                  key={scene.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, scene.id)}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, scene.id)}
                >
                <motion.div
                  className={`group relative glass-card p-0 overflow-hidden cursor-pointer transition-all duration-200 ${
                    draggingSceneId === scene.id ? 'opacity-50 scale-95' : ''
                  } ${
                    draggingSceneId && draggingSceneId !== scene.id ? 'border-2 border-dashed border-purple-500/50' : ''
                  }`}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.03 }}
                >
                  {/* Scene Thumbnail - Click to open lightbox */}
                  <div 
                    className="relative overflow-hidden bg-primary-800"
                    style={{ aspectRatio: getAspectRatio() }}
                    onClick={() => scene.thumbnailUrl && openLightbox(scene)}
                  >
                    {scene.thumbnailUrl ? (
                      <>
                        <img 
                          src={scene.thumbnailUrl} 
                          alt={scene.title}
                          className="w-full h-full object-cover"
                        />
                        {/* Hover Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                          {/* Top indicators */}
                          <div className="absolute top-2 left-2 z-10">
                            <span className="glass-button px-2 py-1 text-xs text-white/80 bg-black/40">
                              #{String(index + 1).padStart(2, '0')}
                            </span>
                          </div>

                          {/* Drag Handle */}
                          <div className="absolute top-2 right-2 z-10">
                            <GripVertical className="w-4 h-4 text-white/60" />
                          </div>

                          {/* Bottom actions */}
                          <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                onSceneSelect(scene.id)
                              }}
                              className="glass-button p-1.5 bg-purple-500/40 hover:bg-purple-500/50"
                              title="Go to scene gallery"
                            >
                              <ExternalLink className="w-3.5 h-3.5 text-white" />
                            </button>
                            <div className="text-xs text-white/80 truncate max-w-[60%]">
                              {scene.title}
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div 
                        className="w-full h-full flex flex-col items-center justify-center cursor-pointer"
                        onClick={() => onSceneSelect(scene.id)}
                      >
                        <Camera className="w-10 h-10 text-primary-500/50 mb-2" />
                        <span className="text-white/50 text-xs">No image</span>
                        {/* Scene number overlay */}
                        <div className="absolute top-2 left-2">
                          <span className="glass-button px-2 py-1 text-xs text-white/70 bg-black/30">
                            #{String(index + 1).padStart(2, '0')}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Scene Title Bar */}
                  <div className="p-2 bg-black/30">
                    <h3 
                      className="font-headline text-white font-medium text-sm truncate cursor-pointer hover:text-purple-300 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation()
                        onSceneSelect(scene.id)
                      }}
                    >
                      {scene.title}
                    </h3>
                  </div>
                </motion.div>
                </div>
              ))}
            </div>

            {/* Empty State */}
            {scenes.length === 0 && (
              <div className="text-center py-16">
                <Camera className="w-16 h-16 text-primary-400 mx-auto mb-4" />
                <p className="font-body font-light text-white/70 mb-4">No scenes yet</p>
                <button
                  onClick={() => setShowCreateScene(true)}
                  className="glass-button bg-purple-500/30 hover:bg-purple-500/40 text-white px-6 py-3"
                >
                  <Plus className="w-5 h-5 inline mr-2" />
                  Create First Scene
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>

      {/* Lightbox - 100% resolution centered with fit toggle and arrows */}
      {currentLightboxScene && (
        <motion.div
          className="fixed inset-0 bg-black/95 z-[60] flex flex-col"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setLightboxIndex(null)}
        >
          {/* Top Bar */}
          <div className="flex-shrink-0 flex items-center justify-between p-4 z-10">
            <div className="text-white">
              <h3 className="font-headline text-xl">{currentLightboxScene.title}</h3>
              <p className="text-white/50 text-sm">
                {lightboxIndex! + 1} of {scenesWithThumbnails.length} • {fitToScreen ? 'Fit to screen' : '100% resolution'}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              {/* Fit/100% Toggle */}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setFitToScreen(!fitToScreen)
                }}
                className="glass-button p-2 hover:bg-white/10 flex items-center space-x-2"
                title={fitToScreen ? 'View at 100%' : 'Fit to screen'}
              >
                {fitToScreen ? (
                  <>
                    <Maximize className="w-5 h-5 text-white" />
                    <span className="text-white text-sm">100%</span>
                  </>
                ) : (
                  <>
                    <Minimize2 className="w-5 h-5 text-white" />
                    <span className="text-white text-sm">Fit</span>
                  </>
                )}
              </button>
              <button
                className="glass-button p-2 hover:bg-white/10"
                onClick={() => setLightboxIndex(null)}
              >
                <X className="w-6 h-6 text-white" />
              </button>
            </div>
          </div>

          {/* Image Container */}
          <div 
            className={`flex-1 overflow-auto flex items-center justify-center p-4 ${
              fitToScreen ? '' : 'items-start'
            }`}
            onClick={() => setLightboxIndex(null)}
          >
            <img
              src={currentLightboxScene.thumbnailUrl}
              alt={currentLightboxScene.title}
              className={fitToScreen ? 'max-w-full max-h-full object-contain' : 'max-w-none m-auto'}
              style={fitToScreen ? {} : { width: 'auto', height: 'auto' }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Navigation Arrows */}
          {lightboxIndex! > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                handlePrevImage()
              }}
              className="fixed left-4 top-1/2 -translate-y-1/2 glass-button p-3 hover:bg-white/20 z-10"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
          )}
          {lightboxIndex! < scenesWithThumbnails.length - 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleNextImage()
              }}
              className="fixed right-4 top-1/2 -translate-y-1/2 glass-button p-3 hover:bg-white/20 z-10"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          )}
        </motion.div>
      )}

      {/* Create Scene Dialog */}
      <CreateSceneDialog
        open={showCreateScene}
        onClose={() => setShowCreateScene(false)}
        projectId={projectId}
      />
    </AnimatePresence>
  )
}

export default ScenesOverviewDialog
